// Pieces the sign-in card, the consent page, Settings -> Sign-in and Your accounts share (mock M2,
// sign-in-methods.html): Google's and Microsoft's own marks, their buttons, the refusals, and adding
// Google or Microsoft to the signed-in account, which keeps it only when it carries the Omni email.
import React from "react";
import { authCode, authEmail, closedWindow, type AuthClient, type Method, type Provider } from "../auth.js";
import { Icon } from "../icons.js";

export const PROVIDER_NAME: Record<Provider, string> = { google: "Google", microsoft: "Microsoft" };

/** Who to ask for an invite, under every way in. */
export const NO_INVITE = "No invite yet? At a dealer, the owner adds you. At Phoneware, a Super admin does.";

/** How a sign-in is named mid-sentence: "the email link", "Google". */
export function methodWords(method: Method): string {
  return method === "link" ? "the email link" : PROVIDER_NAME[method];
}

/**
 * How Identity Platform's firebase.sign_in_provider reads, as Omni names it. The email link is Identity Platform's
 * "password" provider; Omni has no passwords. Anything else is named as Identity Platform sent it.
 */
export function methodOf(provider: string): Method | null {
  if (provider === "password" || provider === "emailLink") return "link";
  if (provider === "google.com") return "google";
  if (provider === "microsoft.com") return "microsoft";
  return null;
}

/** Google's and Microsoft's own marks, as their sign-in button guidelines ask for. */
export function ProviderMark({ provider, size = 18 }: { provider: Provider; size?: number }): React.ReactElement {
  return (
    <svg className="mark" width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      {provider === "google" ? (
        <>
          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
        </>
      ) : (
        <>
          <rect x="1" y="1" width="21" height="21" fill="#F25022" />
          <rect x="26" y="1" width="21" height="21" fill="#7FBA00" />
          <rect x="1" y="26" width="21" height="21" fill="#00A4EF" />
          <rect x="26" y="26" width="21" height="21" fill="#FFB900" />
        </>
      )}
    </svg>
  );
}

/** "Continue with Google", "Add Microsoft": white, with the provider's mark, the same height as the email button. */
export function IdpButton({ provider, label, onClick, disabled, testId }: { provider: Provider; label: string; onClick: () => void; disabled?: boolean; testId?: string }): React.ReactElement {
  return (
    <button className="idp-btn" type="button" data-testid={testId} disabled={disabled} onClick={onClick}>
      <ProviderMark provider={provider} />
      {label}
    </button>
  );
}

/** Why adding Google or Microsoft stopped, as the card or the methods card says it. */
export type LinkAlert =
  | { kind: "different"; provider: Provider; email: string | null }
  | { kind: "taken"; provider: Provider }
  | { kind: "ms-closed" }
  | { kind: "problem"; text: string };

/** A refusal callout: a bold first line, then what to do. */
export function Refusal({ kind, icon, title, testId, children }: { kind: "stop" | "warn"; icon: "user-x" | "info" | "triangle-alert" | "ban"; title: React.ReactNode; testId?: string; children: React.ReactNode }): React.ReactElement {
  return (
    <div className={`callout ${kind} titled`} role="alert" data-testid={testId}>
      <Icon name={icon} size={16} />
      <span>
        <b>{title}</b>
        {children}
      </span>
    </div>
  );
}

/** The refusal for adding a provider, about the signed-in person and their Omni email. */
export function LinkAlertView({ alert, omniEmail }: { alert: LinkAlert; omniEmail: string }): React.ReactElement {
  switch (alert.kind) {
    case "different": {
      const P = PROVIDER_NAME[alert.provider];
      return (
        <Refusal kind="stop" icon="triangle-alert" title={alert.email ? `That ${P} account is ${alert.email}` : `That ${P} account has no email`} testId="link-different">
          It has to be {omniEmail}, so Omni didn&rsquo;t add it.
        </Refusal>
      );
    }
    case "taken":
      return (
        <Refusal kind="stop" icon="ban" title={`That ${PROVIDER_NAME[alert.provider]} account is already in use`} testId="link-taken">
          Another Omni sign-in has it, so Omni didn&rsquo;t add it. Nothing changed. Phoneware can look into which one.
        </Refusal>
      );
    case "ms-closed":
      return (
        <Refusal kind="warn" icon="info" title="Microsoft didn&rsquo;t sign you in" testId="link-ms-closed">
          Omni takes work or school Microsoft accounts only, so it can&rsquo;t add a personal one, like outlook.com or hotmail.com. The email link still works.
        </Refusal>
      );
    case "problem":
      return (
        <Refusal kind="stop" icon="triangle-alert" title="That didn&rsquo;t work" testId="link-problem">
          {alert.text}
        </Refusal>
      );
    default: {
      const unhandled: never = alert;
      throw new Error(`Unknown alert ${String(unhandled)}`);
    }
  }
}

/** A sign-in or a change that stopped for a reason Omni has no card for, in plain words with the code for support. */
export function signInProblem(err: unknown): string {
  switch (authCode(err)) {
    case "auth/popup-blocked":
      return "Your browser blocked the sign-in window. Allow pop-ups for this site, then try again.";
    case "auth/network-request-failed":
      return "Omni couldn\u2019t reach the sign-in service. Check your connection, then try again.";
    case "auth/too-many-requests":
      return "Too many tries. Wait a minute, then try again.";
    case "auth/requires-recent-login":
      return "This change needs a fresh sign-in. Sign out, sign in again, then try it.";
    case "auth/user-disabled":
      return "This sign-in is turned off. Ask whoever invited you to turn it back on.";
    case "auth/operation-not-allowed":
      return "That sign-in isn\u2019t turned on for Omni yet. Use the email link.";
    case "":
      return "Something went wrong. Try again.";
    default:
      return `Something went wrong (${authCode(err)}). Try again.`;
  }
}

/** What adding Google or Microsoft came to. */
export type AddOutcome = { kind: "added"; email: string } | { kind: "refused"; alert: LinkAlert } | { kind: "closed" };

/**
 * Adds Google or Microsoft to the signed-in account in its popup (plan 3.6). Omni keeps it only when the account carries
 * the Omni email: any other is taken straight back off, so it never signs anyone in. Closing Google's window says
 * nothing; closing Microsoft's says why it may have refused, since Omni can't tell that from a plain close.
 */
export async function addProvider(auth: AuthClient, provider: Provider, omniEmail: string): Promise<AddOutcome> {
  const want = omniEmail.toLowerCase();
  let email: string | null;
  try {
    email = (await auth.link(provider)).email;
  } catch (err) {
    if (authCode(err) === "auth/credential-already-in-use") return { kind: "refused", alert: { kind: "taken", provider } };
    if (authCode(err) === "auth/provider-already-linked") return { kind: "added", email: auth.linkedProviders().find((p) => p.provider === provider)?.email ?? want };
    if (closedWindow(err)) return provider === "microsoft" ? { kind: "refused", alert: { kind: "ms-closed" } } : { kind: "closed" };
    return { kind: "refused", alert: { kind: "problem", text: signInProblem(err) } };
  }
  if (email === want) return { kind: "added", email };
  try {
    await auth.unlink(provider);
  } catch (err) {
    return { kind: "refused", alert: { kind: "problem", text: `Omni couldn\u2019t take that ${PROVIDER_NAME[provider]} account back off. Remove it in Settings, Sign-in. ${signInProblem(err)}` } };
  }
  return { kind: "refused", alert: { kind: "different", provider, email } };
}

/** The email a refused provider sign-in names: Identity Platform's, when it gives one. */
export const refusedEmail = authEmail;
